import React from 'react';
import { Box, Pagesection, Sprite } from '@ktag/styleguide/dist/react';
import { useTranslation } from 'react-i18next';

import { Statistics as StatisticsType } from 'api/api.types';
import classNames from 'classnames';

const STATISTICS_ATTRIBUTES = [
  'elected_count',
  'reelected_count',
  'newly_elected_count',
  'average_age',
] as const;

interface Props {
  currentYear: number;
  currentStatistics: StatisticsType;
  previousYear?: number;
  previousStatistics?: StatisticsType;
  openSeats: number;
}

// "average_age" is a string instead of a number and has a lot of decimals
const getFormattedValue = (value: string | number, showFirstDecimal: boolean) => {
  if (!showFirstDecimal) {
    return value;
  }

  return Number(value).toFixed(1);
};

const Statistics = ({
  currentYear,
  currentStatistics,
  previousYear,
  previousStatistics,
  openSeats,
}: Props) => {
  const { t } = useTranslation('wap');

  const getAttributeTransKey = (attribute: (typeof STATISTICS_ATTRIBUTES)[number]) => {
    switch (attribute) {
      case 'elected_count':
        return 'statistics_seats';
      case 'reelected_count':
        return 'statistics_reelected';
      case 'newly_elected_count':
        return 'statistics_newly_elected';
      case 'average_age':
        return 'statistics_avg_age';
      default:
        return '';
    }
  };

  // if we don't have statistics for the previous year, we only display the current year
  // and flip the table (label to the left side, current results on the right side, left side stats hidden)
  const rightSideStats = previousStatistics ?? currentStatistics;
  const rightSideYear = previousStatistics ? previousYear : currentYear;

  return (
    <Pagesection additionalClassNames="pagesection__statistics">
      <table className="statistics">
        <thead>
          {previousStatistics && (
            <tr>
              <th colSpan={3} className="statistics__years">
                {currentYear}
              </th>
              <th aria-hidden />
              <th colSpan={3} className="statistics__years">
                {previousYear}
              </th>
            </tr>
          )}
          <tr className="statistics__visuallyhidden">
            {previousStatistics && (
              <>
                <th>{t('statistics_female', { year: currentYear })}</th>
                <th>{t('statistics_male', { year: currentYear })}</th>
                <th>{t('statistics_total', { year: currentYear })}</th>
              </>
            )}
            <th>{t('statistics_attribute')}</th>
            <th>{t('statistics_total', { year: rightSideYear })}</th>
            <th>{t('statistics_female', { year: rightSideYear })}</th>
            <th>{t('statistics_male', { year: rightSideYear })}</th>
          </tr>
        </thead>
        <tbody>
          {STATISTICS_ATTRIBUTES.map((key) => (
            <tr className="statistics__row" key={key}>
              {previousStatistics && (
                <>
                  <td className="statistics__valuefieldcell">
                    <div className="statistics__valuefield">
                      <Sprite id="male" />
                      {getFormattedValue(currentStatistics.m[key], key === 'average_age')}
                    </div>
                  </td>
                  <td className="statistics__valuefieldcell">
                    <div className="statistics__valuefield">
                      <Sprite id="female" />
                      {getFormattedValue(currentStatistics.w[key], key === 'average_age')}
                    </div>
                  </td>
                  <td className="statistics__total statistics__total--current">
                    {getFormattedValue(
                      currentStatistics.total[key],
                      key === 'average_age',
                    )}
                  </td>
                </>
              )}
              <th
                className={classNames('statistics__attribute', {
                  'statistics__attribute--align-left': !previousStatistics,
                })}
              >
                {t(getAttributeTransKey(key))}
              </th>
              <td className="statistics__total statistics__total--previous">
                {getFormattedValue(rightSideStats.total[key], key === 'average_age')}
              </td>
              <td className="statistics__valuefieldcell">
                <div className="statistics__valuefield">
                  <Sprite id="male" />
                  {getFormattedValue(rightSideStats.m[key], key === 'average_age')}
                </div>
              </td>
              <td className="statistics__valuefieldcell">
                <div className="statistics__valuefield">
                  <Sprite id="female" />
                  {getFormattedValue(rightSideStats.w[key], key === 'average_age')}
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {openSeats > 0 && (
        <Box additionalClassNames="statistics__open-seats">
          {t('statistics_open_seats', { value: openSeats })}
        </Box>
      )}
    </Pagesection>
  );
};

export default Statistics;
